import type { FC, ReactNode } from 'react'
import type { AppProps } from 'next/app'
import type { TransitionPhase } from '@/lib/universe/store'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { collapseGravity } from '@/lib/universe/gravity'
import { useMenu } from '@/components/generals/menu-context'

import {
    beginEnter,
    beginLeave,
    cancelLeave,
    finishEnter
} from '@/lib/universe/store'

interface StageEntry {
    Component: AppProps['Component']
    pageProps: AppProps['pageProps']
    key: string
}

interface PageTransitionProps {
    Component: AppProps['Component']
    pageProps: AppProps['pageProps']
}

const normalize = (path: string): string => path.split('#')[0]

const isCompact = (): boolean => typeof window !== 'undefined' && window.innerWidth < 768

const leaveDuration = (): number => (isCompact() ? 520 : 720)

const enterDuration = (): number => (isCompact() ? 640 : 860)

// Keeps the old page on screen while it dims and stretches towards the hole and the disk
// collapses to its ring, then lets the new one fade up out of the dark as the camera glides
// to its viewpoint. Works the same for links, the menu and the back and forward buttons.
export const PageTransition: FC<PageTransitionProps> = ({ Component, pageProps }): ReactNode => {
    const router = useRouter()
    const { open: menuOpen } = useMenu()
    const routeKey = normalize(router.asPath)
    const [stage, setStage] = useState<StageEntry>({ Component, pageProps, key: routeKey })
    const [phase, setPhase] = useState<TransitionPhase>('entering')
    const [hidden, setHidden] = useState(false)
    const stageKeyRef = useRef(routeKey)
    const leaveStartRef = useRef<number | null>(null)
    const menuOpenRef = useRef(menuOpen)
    const menuWasOpenRef = useRef(false)
    const menuClosedAtRef = useRef(-Infinity)

    menuOpenRef.current = menuOpen

    useEffect(() => {
        if (menuWasOpenRef.current && !menuOpen) menuClosedAtRef.current = performance.now()

        menuWasOpenRef.current = menuOpen
    }, [menuOpen])

    const startLeave = useCallback((url: string): void => {
        if (leaveStartRef.current !== null) {
            beginLeave(url)

            return
        }

        leaveStartRef.current = performance.now()
        beginLeave(url)

        // Leaving from the menu: the page has already collapsed, keep it out of sight
        const fromMenu = menuOpenRef.current || performance.now() - menuClosedAtRef.current < 600

        if (fromMenu) setHidden(true)
        else collapseGravity(leaveDuration())

        setPhase('leaving')
    }, [])

    useEffect(() => {
        if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual'
    }, [])

    useEffect(() => {
        const handleStart = (url: string, options: { shallow: boolean }): void => {
            if (options?.shallow) return
            if (normalize(url) === stageKeyRef.current) return

            startLeave(url)
        }

        const handleError = (error: { cancelled?: boolean }): void => {
            if (error?.cancelled) return

            leaveStartRef.current = null
            setHidden(false)
            cancelLeave(stageKeyRef.current)
            setPhase('entering')
        }

        router.events.on('routeChangeStart', handleStart)
        router.events.on('routeChangeError', handleError)

        return () => {
            router.events.off('routeChangeStart', handleStart)
            router.events.off('routeChangeError', handleError)
        }
    }, [router.events, startLeave])

    useEffect(() => {
        if (routeKey === stageKeyRef.current) {
            setStage(previous => (
                previous.Component === Component && previous.pageProps === pageProps
                    ? previous
                    : { Component, pageProps, key: routeKey }
            ))

            return
        }

        if (leaveStartRef.current === null) startLeave(routeKey)

        const elapsed = performance.now() - (leaveStartRef.current ?? performance.now())
        const timer = window.setTimeout(() => {
            leaveStartRef.current = null
            stageKeyRef.current = routeKey
            beginEnter(routeKey)
            window.scrollTo(0, 0)
            setHidden(false)
            setStage({ Component, pageProps, key: routeKey })
            setPhase('entering')
        }, Math.max(0, leaveDuration() - elapsed))

        return () => window.clearTimeout(timer)
    }, [Component, pageProps, routeKey, startLeave])

    useEffect(() => {
        if (phase !== 'entering') return

        const timer = window.setTimeout(() => {
            setPhase('idle')
            finishEnter()
        }, enterDuration() + 200)

        return () => window.clearTimeout(timer)
    }, [phase, stage.key])

    const Displayed = stage.Component

    return (
        <>
            <div id='umbra-stage' className='relative'>
                <div
                    key={stage.key}
                    data-stage-current=''
                    data-phase={phase}
                    data-menu={menuOpen ? 'open' : 'closed'}
                    data-hidden={hidden ? 'true' : 'false'}
                    inert={menuOpen || phase === 'leaving'}
                    className='umbra-page'
                >
                    <Displayed {...stage.pageProps} />
                </div>
            </div>
        </>
    )
}
